import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { DeviceMode } from '../wizard/DeviceSwitcher';
import type { AnchorRegistration, AnchorSpec, DeviceRole } from './types';

/**
 * State shared between the persistent preview host and the wizard surfaces that
 * drive it. Kept DELIBERATELY separate from the data-heavy `ConfiguratorContext`
 * so the host's per-frame rect churn never re-renders the steps, and a device
 * toggle re-renders only the host + the switcher (not the whole wizard).
 */
interface PreviewStageContextValue {
  /** Register an anchor element + its placement spec; returns an unregister fn. */
  register: (id: number, spec: AnchorSpec, element: HTMLElement) => () => void;
  /** Update an already-registered anchor's spec in place (no churn on the host). */
  updateSpec: (id: number, spec: AnchorSpec) => void;
  /** Snapshot of every live registration the host iterates over. */
  registrations: () => AnchorRegistration[];
  /** Bumped on every registry mutation so the host re-derives placement. */
  registryVersion: number;

  /** The wizard's single-mode device toggle (which device is on stage in steps 1-3). */
  device: DeviceMode;
  setDevice: (device: DeviceMode) => void;

  /** Whether the full-screen Live Preview is open, and which device it shows. */
  fullscreenOpen: boolean;
  fullscreenDevice: DeviceMode;
  openFullscreen: () => void;
  closeFullscreen: () => void;
  setFullscreenDevice: (device: DeviceMode) => void;

  /** The host's fixed container — the portal target the overlay renders its chrome
   *  into so it shares the host's stacking context (above the floated iframe). */
  containerNode: HTMLElement | null;
  setContainerNode: (node: HTMLElement | null) => void;

  /** The current surface's clip pane (or null). Devices whose spec opts into `clip`
   *  are cut to this rect by the host, so a device sized to bleed past its panel is
   *  clipped at the panel edge rather than running to the viewport edge. At most one
   *  surface (hence one clip pane) is mounted at a time. */
  clipNode: HTMLElement | null;
  setClipNode: (node: HTMLElement | null) => void;
}

const PreviewStageContext = createContext<PreviewStageContextValue | null>(null);

/** Monotonic anchor id source (module scope; ids never need to survive a reload). */
let nextAnchorId = 1;
export function allocateAnchorId(): number {
  return nextAnchorId++;
}

export function PreviewStageProvider({ children }: { children: ReactNode }): React.ReactElement {
  // The registry is a ref (mutated imperatively by anchors) + a version counter the
  // host subscribes to — so registering/measuring never triggers a React tree walk
  // beyond the host itself.
  const registryRef = useRef<Map<number, AnchorRegistration>>(new Map());
  const [registryVersion, setRegistryVersion] = useState(0);
  const bump = useCallback(() => setRegistryVersion((v) => v + 1), []);

  const [device, setDevice] = useState<DeviceMode>('mobile');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenDevice, setFullscreenDevice] = useState<DeviceMode>('desktop');
  const [containerNode, setContainerNode] = useState<HTMLElement | null>(null);
  const [clipNode, setClipNode] = useState<HTMLElement | null>(null);

  const register = useCallback(
    (id: number, spec: AnchorSpec, element: HTMLElement): (() => void) => {
      registryRef.current.set(id, { id, spec, element });
      bump();
      return () => {
        registryRef.current.delete(id);
        bump();
      };
    },
    [bump],
  );

  const updateSpec = useCallback(
    (id: number, spec: AnchorSpec): void => {
      const existing = registryRef.current.get(id);
      if (!existing) return;
      registryRef.current.set(id, { ...existing, spec });
      bump();
    },
    [bump],
  );

  const registrations = useCallback((): AnchorRegistration[] => [...registryRef.current.values()], []);

  const openFullscreen = useCallback(() => setFullscreenOpen(true), []);
  const closeFullscreen = useCallback(() => setFullscreenOpen(false), []);

  const value = useMemo<PreviewStageContextValue>(
    () => ({
      register,
      updateSpec,
      registrations,
      registryVersion,
      device,
      setDevice,
      fullscreenOpen,
      fullscreenDevice,
      openFullscreen,
      closeFullscreen,
      setFullscreenDevice,
      containerNode,
      setContainerNode,
      clipNode,
      setClipNode,
    }),
    [
      register,
      updateSpec,
      registrations,
      registryVersion,
      device,
      fullscreenOpen,
      fullscreenDevice,
      openFullscreen,
      closeFullscreen,
      containerNode,
      clipNode,
    ],
  );

  return <PreviewStageContext.Provider value={value}>{children}</PreviewStageContext.Provider>;
}

export function usePreviewStage(): PreviewStageContextValue {
  const ctx = useContext(PreviewStageContext);
  if (!ctx) throw new Error('usePreviewStage must be used within a PreviewStageProvider');
  return ctx;
}

/**
 * Registers one device anchor with the host. Returns a ref to attach to the empty,
 * correctly-sized anchor box a surface renders where its device should appear. The
 * host floats the matching warm iframe over the measured box per `spec`. The spec
 * is updated in place on change (no re-register), so the host keeps the same iframe.
 */
export function useDeviceAnchor(spec: AnchorSpec): React.RefCallback<HTMLElement> {
  const { register, updateSpec } = usePreviewStage();
  const idRef = useRef<number>(0);
  if (idRef.current === 0) idRef.current = allocateAnchorId();
  const id = idRef.current;
  const elementRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  // Latest spec held in a ref so the ref-callback can register with it without
  // re-running when only the spec changes (that path goes through updateSpec).
  const specRef = useRef(spec);
  specRef.current = spec;

  // A primitive identity for the spec so the sync effect fires ONLY on a real field
  // change, not on every render (the caller recreates the spec object each render).
  const specKey = `${spec.role}|${spec.mode}|${spec.bezel}|${spec.parkDir ?? ''}|${spec.phoneZoomWidth ?? ''}|${spec.elevate ?? ''}|${spec.clip ?? ''}|${spec.unified ?? ''}`;

  // Push genuine spec edits (bezel / zoom) to the host. Most toggles need none: the
  // host arbitrates "on stage vs parked" from the live `device` / `fullscreen` state.
  useEffect(() => {
    if (elementRef.current) updateSpec(id, specRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, specKey, updateSpec]);

  return useCallback(
    (node: HTMLElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      elementRef.current = node;
      if (node) cleanupRef.current = register(id, specRef.current, node);
    },
    [id, register],
  );
}

/**
 * Publishes the calling surface's clip pane to the host. Attach the returned ref to
 * the panel that floated devices (those with `clip` in their spec) should be cut to.
 * The host measures this element and applies a matching `clip-path`, so a device
 * sized to bleed past the panel (e.g. the finish laptop) is clipped at the panel
 * edge — its own `overflow: hidden` can't, since the device floats in the host's
 * viewport-fixed layer. Clears on unmount so other surfaces don't inherit it.
 */
export function usePreviewClip(): React.RefCallback<HTMLElement> {
  const { setClipNode } = usePreviewStage();
  return useCallback(
    (node: HTMLElement | null) => {
      setClipNode(node);
    },
    [setClipNode],
  );
}

export type { DeviceRole };
